import React, { useEffect, useRef, useState } from "react";
import "./doctorForm.scss";
import { Form, Formik, FormikValues } from "formik";
import { Button, Checkbox, Col, Modal, Row, Switch, Tooltip } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import InputField from "../../../shared/components/InputField";
import { doctorFormValidation } from "./doctorFormValidation";
import { Doctor } from "../../../models/Doctor/doctor.model";
import FileUpload from "../../../shared/components/FileUpload";
import DoctorService from "../../../services/Doctor/doctor.service";

interface ScanCenterOption {
  id: number;
  name: string;
  selected: boolean;
}

interface ScanCenterAdminOption {
  id: number;
  name: string;
  selected: boolean;
}

interface DoctorFormProps {
  doctor: Doctor;
  onSuccess: (doctor: Doctor) => void;
}

function DoctorForm({ doctor, onSuccess }: DoctorFormProps) {
  const [formValues, setFormValues] = useState<Doctor>(doctor);
  const [formLoading, setFormLoading] = useState(false);

  // Override approval scan centers modal state
  const [overrideModalVisible, setOverrideModalVisible] = useState(false);
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideScanCenters, setOverrideScanCenters] = useState<ScanCenterOption[]>([]);
  const [selectedOverrideScanCenterIds, setSelectedOverrideScanCenterIds] = useState<number[]>([]);
  const [overrideScanCenterAdmins, setOverrideScanCenterAdmins] = useState<ScanCenterAdminOption[]>([]);
  const [selectedOverrideScanCenterAdminIds, setSelectedOverrideScanCenterAdminIds] = useState<number[]>([]);
  const overrideDoctorIdRef = useRef<number | null>(null);

  // Persisted whitelist count for this doctor, independent of the modal's
  // in-flight selection state. Used to gate the main Update button so that
  // flipping on "Override Approval" without whitelisting any scan center /
  // scan center admin (which silently leaves the doctor requiring approval
  // everywhere — see Doctor#approval_required_for_scan_center? on the API)
  // cannot be saved. `null` = not yet fetched.
  const [overrideWhitelistCount, setOverrideWhitelistCount] = useState<number | null>(null);

  const handleSubmit = (values: FormikValues) => {
    const doctor = Object.assign(new Doctor(), values);
    setFormLoading(true);
    if (doctor.id) {
      DoctorService.updateDoctor(
        doctor,
        (doctor: Doctor) => {
          onSuccess(doctor);
        },
        () => {},
        () => {
          setFormLoading(false);
        }
      );
    } else {
      doctor.password = `${doctor?.firstName || ""}${doctor?.lastName || ""}`;
      DoctorService.createDoctor(
        doctor,
        (doctor: Doctor) => {
          onSuccess(doctor);
        },
        () => {},
        () => {
          setFormLoading(false);
        }
      );
    }
  };

  // Refreshes `overrideWhitelistCount` from the API without opening the modal.
  // Called on mount (for existing doctors) and again after the modal saves.
  const refreshOverrideWhitelistCount = (doctorId: number) => {
    DoctorService.fetchApprovalOverrideScanCenters(
      doctorId,
      (data: any) => {
        const scIds: number[] = data.selected_scan_center_ids ?? [];
        const scaIds: number[] = data.selected_scan_center_admin_ids ?? [];
        setOverrideWhitelistCount(scIds.length + scaIds.length);
      },
      () => {
        setOverrideWhitelistCount(0);
      },
      () => {}
    );
  };

  const openOverrideModal = (doctorId: number) => {
    overrideDoctorIdRef.current = doctorId;
    DoctorService.fetchApprovalOverrideScanCenters(
      doctorId,
      (data: any) => {
        setOverrideScanCenters(data.eligible_scan_centers ?? []);
        setSelectedOverrideScanCenterIds(data.selected_scan_center_ids ?? []);
        setOverrideScanCenterAdmins(data.eligible_scan_center_admins ?? []);
        setSelectedOverrideScanCenterAdminIds(data.selected_scan_center_admin_ids ?? []);
        setOverrideModalVisible(true);
      },
      () => {},
      () => {}
    );
  };

  const saveOverrideSettings = () => {
    const doctorId = overrideDoctorIdRef.current;
    if (!doctorId) return;
    setOverrideSaving(true);
    DoctorService.updateApprovalOverrideScanCenters(
      doctorId,
      true,
      selectedOverrideScanCenterIds,
      selectedOverrideScanCenterAdminIds,
      () => {
        setOverrideWhitelistCount(
          selectedOverrideScanCenterIds.length + selectedOverrideScanCenterAdminIds.length
        );
        setOverrideModalVisible(false);
      },
      () => {},
      () => {
        setOverrideSaving(false);
      }
    );
  };

  useEffect(() => {
    if (doctor.id) {
      DoctorService.showDoctor(
        doctor.id,
        (doctor: Doctor) => {
          setFormValues(doctor);
          // Preload whitelist count so the Update-button gate reflects the
          // persisted state even if the admin never opens the configure
          // modal in this session.
          if (doctor.overrideApprovalForScanCenters && doctor.id) {
            refreshOverrideWhitelistCount(doctor.id);
          } else {
            setOverrideWhitelistCount(null);
          }
        },
        () => {},
        () => {}
      );
    }
  }, [doctor]);

  return (
    <div className="doctor-form">
      <h2 className="text-primary">
        {doctor.id ? "Edit Doctor" : "Add Doctor"}
      </h2>
      <Formik
        initialValues={formValues}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={doctorFormValidation}
      >
        {({
          handleSubmit,
          handleChange,
          values,
          errors,
          isValid,
          dirty,
          setFieldValue,
        }) => {
          return (
            <Form>
              <Row gutter={[30, 0]}>
                <Col span={12}>
                  <InputField
                    title="First Name"
                    type="text"
                    name="firstName"
                    placeholder="Enter First name"
                  />
                </Col>
                <Col span={12}>
                  <InputField
                    title="Last Name"
                    type="text"
                    name="lastName"
                    placeholder="Enter Last name"
                  />
                </Col>
                <Col span={12}>
                  <InputField
                    title="Email"
                    type="email"
                    name="email"
                    placeholder="Enter Email"
                  />
                </Col>
                <Col span={12}>
                  <InputField
                    title="Mobile"
                    type="text"
                    name="mobileNumber"
                    placeholder="Enter Mobile"
                  />
                </Col>
                <Col span={12}>
                  <InputField
                    title="Address"
                    type="text"
                    name="address"
                    placeholder="Enter Address"
                  />
                </Col>
                <Col span={12}>
                  <InputField
                    title="Specialisation"
                    type="text"
                    name="specialisation"
                    placeholder="Enter Specialisation"
                  />
                </Col>
                <Col span={12}>
                  <InputField
                    title="State of registration"
                    type="text"
                    name="stateOfRegistration"
                    placeholder="Enter State of registration"
                  />
                </Col>
                <Col span={12}>
                  <InputField
                    title="Medical council registration number"
                    type="text"
                    name="medicalCouncilRegistrationNumber"
                    placeholder="Enter Registration number"
                  />
                </Col>
                <Col span={12}>
                  <InputField
                    title="Assignment limit/ week"
                    type="number"
                    name="assignmentsPerWeek"
                    placeholder="Enter Assignment Limit"
                  />
                </Col>
                {!values?.id && (
                  <Col span={12}>
                    <InputField
                      readOnly
                      title="Your Generated Password"
                      type="text"
                      value={`${values?.firstName || ""}${
                        values?.lastName || ""
                      }`}
                      name="password"
                      placeholder="Your Password"
                    />
                  </Col>
                )}
                <Col span={12}>
                  <div className="doctor-form__switch">
                    <div className="doctor-form__switch-title">
                      Approval Required?
                    </div>
                    <Switch
                      checked={values?.approvalRequired}
                      onChange={(checked) => {
                        setFieldValue("approvalRequired", checked);
                        if (!checked) {
                          setFieldValue("overrideApprovalForScanCenters", false);
                        }
                      }}
                    />
                  </div>
                </Col>
                {values?.approvalRequired && (() => {
                  // The whitelist cannot be configured until the doctor has an
                  // id (the configure modal hits /admin/doctors/:id/...).
                  // Toggling the switch on pre-save would silently persist a
                  // `override_approval_for_scan_centers = true` with no
                  // whitelist entries, which on the backend still forces
                  // approval everywhere — the opposite of what the admin
                  // expects. So block it and explain why.
                  const isNewDoctor = !values?.id;
                  const overrideOn = !!values?.overrideApprovalForScanCenters;
                  // Unconfigured = switch is on for an existing doctor AND the
                  // persisted whitelist is empty (count has been fetched).
                  const overrideUnconfigured =
                    overrideOn &&
                    !isNewDoctor &&
                    overrideWhitelistCount === 0;
                  return (
                    <Col span={12}>
                      <div className="doctor-form__switch">
                        <div className="doctor-form__switch-title">
                          Override approval for specific scan centers?
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Tooltip
                            title={
                              isNewDoctor
                                ? "Save the doctor first, then enable override and configure the scan centers."
                                : ""
                            }
                          >
                            <Switch
                              checked={overrideOn}
                              disabled={isNewDoctor}
                              onChange={(checked) => {
                                setFieldValue("overrideApprovalForScanCenters", checked);
                                if (checked && values?.id) {
                                  // Ensure the gate reflects real persisted
                                  // state the moment the switch flips on.
                                  refreshOverrideWhitelistCount(values.id);
                                }
                                if (!checked) {
                                  setOverrideWhitelistCount(null);
                                }
                              }}
                            />
                          </Tooltip>
                          {values?.id && overrideOn && (
                            <Button
                              size="small"
                              type={overrideUnconfigured ? "primary" : "default"}
                              danger={overrideUnconfigured}
                              onClick={() => openOverrideModal(values.id!)}
                            >
                              Configure scan centers
                            </Button>
                          )}
                        </div>
                        {overrideUnconfigured && (
                          <div
                            style={{
                              marginTop: 6,
                              color: "#d4380d",
                              fontSize: 12,
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <ExclamationCircleOutlined />
                            <span>
                              Select at least one scan center or scan center admin before saving —
                              otherwise direct publish stays disabled everywhere.
                            </span>
                          </div>
                        )}
                      </div>
                    </Col>
                  );
                })()}
                <Col span={12}>
                  <FileUpload
                    name="doctorSignature"
                    title="Upload Signature"
                    setFieldValue={setFieldValue}
                    value={values.doctorSignature}
                  />
                </Col>
              </Row>
              <div className="doctor-form__submit-wrapper text-right mt-3">
                {(() => {
                  // Block Update when Override Approval is on for an existing
                  // doctor but the whitelist is empty. Without this the admin
                  // can save a state that — on the backend — is equivalent to
                  // "approval required everywhere" and wastes a save cycle.
                  const overrideBlocksSubmit =
                    !!values?.id &&
                    !!values?.approvalRequired &&
                    !!values?.overrideApprovalForScanCenters &&
                    overrideWhitelistCount === 0;
                  return (
                    <Tooltip
                      title={
                        overrideBlocksSubmit
                          ? "Configure at least one scan center (or scan center admin) for override approval before updating."
                          : ""
                      }
                    >
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={formLoading}
                        disabled={!isValid || formLoading || overrideBlocksSubmit}
                      >
                        {doctor?.id ? "Update" : "Add"}
                      </Button>
                    </Tooltip>
                  );
                })()}
              </div>
            </Form>
          );
        }}
      </Formik>

      <Modal
        title="Override approval — scan centers & scan center admins"
        visible={overrideModalVisible}
        onCancel={() => setOverrideModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setOverrideModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={overrideSaving}
            onClick={saveOverrideSettings}
          >
            Save
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ marginBottom: 8, color: "#555" }}>
            <strong>Direct scan centers:</strong> select centers for which this doctor can{" "}
            <strong>directly publish</strong> reports without admin approval:
          </p>
          {overrideScanCenters.length === 0 ? (
            <p style={{ color: "#999" }}>No scan centers available.</p>
          ) : (
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              {overrideScanCenters.map((sc) => (
                <div key={sc.id} style={{ marginBottom: 6 }}>
                  <Checkbox
                    checked={selectedOverrideScanCenterIds.includes(sc.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedOverrideScanCenterIds((prev) => [...prev, sc.id]);
                      } else {
                        setSelectedOverrideScanCenterIds((prev) =>
                          prev.filter((id) => id !== sc.id)
                        );
                      }
                    }}
                  >
                    {sc.name}
                  </Checkbox>
                </div>
              ))}
            </div>
          )}
        </div>
        {overrideScanCenterAdmins.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ marginBottom: 8, color: "#555" }}>
              <strong>Scan center admins (territory):</strong> override applies to{" "}
              <strong>all cases</strong> from scan centers owned by the selected admin
              (not individual child centers).
            </p>
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              {overrideScanCenterAdmins.map((a) => (
                <div key={a.id} style={{ marginBottom: 6 }}>
                  <Checkbox
                    checked={selectedOverrideScanCenterAdminIds.includes(a.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedOverrideScanCenterAdminIds((prev) => [...prev, a.id]);
                      } else {
                        setSelectedOverrideScanCenterAdminIds((prev) =>
                          prev.filter((id) => id !== a.id)
                        );
                      }
                    }}
                  >
                    {a.name}
                  </Checkbox>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default DoctorForm;
